import { useMemo, useEffect, useRef } from 'react';
import { C } from '../../../lib/tokens.js';
import { Spinner } from '../../../components/ui/Misc.jsx';
import { t } from '../../../lib/i18n.jsx';

// Filters out null/broken records once, shared by every view.
export function useValidRequests(requests) {
  return useMemo(() => (requests || []).filter(Boolean), [requests]);
}

export function useScrollNearBottom(scrollRef, onNearBottom, enabled, threshold = 200) {
  const cbRef = useRef(onNearBottom);
  useEffect(() => { cbRef.current = onNearBottom; });
  useEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < threshold) cbRef.current?.();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef, enabled, threshold]);
}

export function LoadingRow() {
  return (
    <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, color: C.iron, fontSize: 12 }}>
      <Spinner size={15} />
      Loading…
    </div>
  );
}

// Wraps the common "scrollable container that loads more near the bottom"
// pattern shared by List, Timeline, and Cards views. Kanban keeps its own
// per-column scroll handling since each column scrolls independently.
export function InfiniteScrollList({ hasMore, onLoadMore, loadingMore, style, children }) {
  const scrollRef = useRef(null);
  useScrollNearBottom(scrollRef, onLoadMore, hasMore && !loadingMore);
  return (
    <div ref={scrollRef} style={style}>
      {children}
      {loadingMore && <LoadingRow />}
    </div>
  );
}
// Small tabs on a main ticket for each request linked to it as "related" (it points at
// this one via R_RequestRelated_ID). Each tab opens that request without opening the card.
export function RelatedTabs({ related, onOpen }) {
  if (!related?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {related.map(r => (
        <span key={r.id} title={r.summary} onClick={e => { e.stopPropagation(); onOpen(r); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px',
            border: `1px solid ${C.mercury}`, borderRadius: 3, background: C.platinum,
            fontSize: 10, fontFamily: 'Roboto Mono', color: C.liquid, cursor: 'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.liquid; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = C.mercury; }}>
          <span className="material-icons" style={{ fontSize: 11 }}>link</span>
          #{r.docNumber ?? r.id}
        </span>
      ))}
    </div>
  );
}


// Sort modes for the kanban board. 'default' keeps the existing order
// (most recently updated first); urgency = priority, most urgent first, then most recently updated.
export const SORT_MODES = ['default', 'urgency', 'updated', 'stale'];
const PRIORITY_RANK = { critical: 0, high: 1, normal: 2, low: 3 };
const updatedTime = (r) => new Date(r.updatedAt).getTime() || 0;
export function sortRequests(items, mode) {
  const byUpdated = (a, b) => updatedTime(b) - updatedTime(a);
  const rank = (r) => PRIORITY_RANK[String(r.priority ?? '').toLowerCase()] ?? PRIORITY_RANK.normal;
  const copy = [...items];
  switch (mode) {
    case 'urgency': return copy.sort((a, b) => rank(a) - rank(b) || byUpdated(a, b));
    case 'stale':   return copy.sort((a, b) => -byUpdated(a, b));
    default:        return copy.sort(byUpdated);
  }
}

// Compact sort dropdown; `value` is one of SORT_MODES.
export function SortSelect({ value, onChange, title, label }) {
  return (
    <label title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.iron }}>
      <span className="material-icons" style={{ fontSize: 14 }}>sort</span>
      {label ? <span>{label}</span> : null}
      <select value={value} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'Roboto', fontSize: 11, padding: '2px 4px', borderRadius: 4, border: `1px solid ${C.mercury}`, background: C.platinum, color: C.fg, outline: 'none', cursor: 'pointer', maxWidth: 150 }}>
        {SORT_MODES.map(m => <option key={m} value={m}>{t(`board.sort.${m}`)}</option>)}
      </select>
    </label>
  );
}
